import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Link,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

const navItems = [
  { label: "Why I'm Running", href: "#why-running" },
  { label: "Issues", href: "#issues" },
  { label: "About", href: "#about" },
  {
    label: "Sign the Petition",
    href: "#petition",
    highlight: true,
    icon: "🔥",
  },
  { label: "Events", href: "#events" },
  { label: "Photos", href: "#photos" },
  { label: "Legislation", href: "#legislation" },
  { label: "District Map", href: "#district-map" },
  { label: "Volunteer", href: "#volunteer" },
  { label: "Contact us", href: "#contact-us" },
];

const UserTopNavbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("#why-running");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  // Automatically update active link on scroll
  useEffect(() => {
    const sections = navItems.map((item) => item.href.substring(1));
    const observerOptions = {
      root: null,
      rootMargin: "-40% 0px -40% 0px",
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveLink(`#${entry.target.id}`);
        }
      });
    }, observerOptions);

    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleNavClick = (href) => {
    setActiveLink(href);
    setDrawerOpen(false);

    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "auto", block: "start" });
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "#0A192F",
          boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
          px: { xs: 1, md: 2 },
          zIndex: 1100,
          transition: "all 0.3s ease",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            minHeight: "52px !important",
            transition: "min-height 0.3s ease",
          }}
        >
          {/* ── Branding ─────────────────────────────────────── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 1,
              cursor: "pointer",
            }}
            onClick={() => handleNavClick("#home")}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "#FFF",
                fontFamily: "'Playfair Display', serif",
                letterSpacing: 1,
                fontSize: "1.1rem",
                transition: "font-size 0.3s ease",
              }}
            >
              TAYLOR
            </Typography>
            <Typography
              sx={{
                color: "#C59324",
                fontWeight: 500,
                fontFamily: "'Playfair Display', serif",
                fontSize: "0.85rem",
                transition: "font-size 0.3s ease",
              }}
            >
              FOR MIDDLETOWN
            </Typography>
          </Box>

          {/* ── Desktop nav links ─────────────────────────────── */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  href={item.href}
                  underline="none"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item.href);
                  }}
                  sx={{
                    color: item.highlight ? "#C59324" : "#FFF",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    transition: "all 0.2s",
                    borderBottom:
                      activeLink === item.href
                        ? `2px solid ${item.highlight ? "#C59324" : "#FFF"}`
                        : "2px solid transparent",
                    pb: "2px",
                    "&:hover": { opacity: 0.75 },
                  }}
                >
                  {item.icon && <span>{item.icon}</span>}
                  {item.label}
                </Link>
              ))}

              <Button
                variant="contained"
                onClick={() => handleNavClick("#donate")}
                sx={{
                  backgroundColor: "#C59324",
                  color: "#FFF",
                  fontWeight: "bold",
                  borderRadius: "4px",
                  px: 2,
                  py: 1,
                  ml: 1,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  fontSize: "0.78rem",
                  transition: "all 0.3s ease",
                  "&:hover": { backgroundColor: "#A67C1E" },
                }}
              >
                Donate Now
              </Button>
            </Box>
          )}

          {/* ── Mobile hamburger ──────────────────────────────── */}
          {isMobile && (
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{ color: "#FFF" }}
              aria-label="Open menu"
            >
              <MenuIcon fontSize="large" />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* ── Mobile Drawer ──────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: { xs: "85vw", sm: 280 }, maxWidth: 280, backgroundColor: "#0A192F", color: "#FFF" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: "bold",
                fontSize: "1.2rem",
              }}
            >
              TAYLOR
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "0.7rem",
                color: "#C59324",
                fontWeight: 500,
              }}
            >
              FOR MIDDLETOWN
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDrawerOpen(false)}
            sx={{ color: "rgba(255,255,255,0.7)" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <List sx={{ pt: 1 }}>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                onClick={() => handleNavClick(item.href)}
                sx={{
                  px: 3,
                  py: 1.25,
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
                  borderLeft:
                    activeLink === item.href
                      ? `4px solid ${item.highlight ? "#C59324" : "#FFF"}`
                      : "4px solid transparent",
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {item.icon && <span>{item.icon}</span>}
                      <Typography
                        sx={{
                          fontSize: "0.95rem",
                          fontWeight: 600,
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mx: 2 }} />

        <Box sx={{ px: 3, py: 2 }}>
          <Button
            fullWidth
            onClick={() => handleNavClick("#donate")}
            sx={{
              backgroundColor: "#C59324",
              color: "#FFF",
              fontWeight: "bold",
              textTransform: "none",
              py: 1.25,
              fontSize: "1rem",
              "&:hover": { backgroundColor: "#A67C1E" },
            }}
          >
            Donate Now
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default UserTopNavbar;
